import {
  BILLING_ENTITY_REQUIRED_CODE,
  ENTERPRISE_IDENTITY_NOT_ENTITLED_CODE,
} from '@reflex/shared';
import { ApiError } from './http';

/**
 * True when `error` is an API refusal carrying exactly `code`.
 *
 * The narrow `instanceof` matters: a thrown `Error` whose message happens to
 * contain the code, or a rejected promise carrying a string, must not read as
 * a refusal. Only a response the server actually shaped counts.
 */
function hasApiCode(error: unknown, code: string): boolean {
  return error instanceof ApiError && error.code === code;
}

/**
 * True when a request failed only because the company's tier does not include
 * enterprise identity.
 *
 * This is an answer, not a failure: nothing is broken, and no amount of
 * retrying changes a subscription tier. Surfaces branch on it so a tier
 * without the feature reads as a plan notice rather than the red "could not
 * load, try again" an outage would produce.
 */
export function isEnterpriseIdentityNotEntitled(error: unknown): boolean {
  return hasApiCode(error, ENTERPRISE_IDENTITY_NOT_ENTITLED_CODE);
}

/**
 * True when a company-scoped request was refused because the active
 * organization is not attached to a company at all.
 *
 * Reachable in the wild, not just in theory: migration `0109` deliberately
 * leaves `organizations.billing_entity_id` nullable for a bake period because
 * the previous image's org create does not stamp it, so an org created by an
 * old pod during a rolling deploy has no company. Like the entitlement
 * refusal, no retry fixes it — only an operator attaching the org.
 */
export function isBillingEntityRequired(error: unknown): boolean {
  return hasApiCode(error, BILLING_ENTITY_REQUIRED_CODE);
}

/**
 * True when the request was refused for a reason no retry can change. The
 * shared test for "render a notice, not a red error box with a Retry".
 */
export function isUnretryableCompanyRefusal(error: unknown): boolean {
  return isEnterpriseIdentityNotEntitled(error) || isBillingEntityRequired(error);
}

/**
 * One sentence, shared by every enterprise-identity surface, so an admin who
 * lands on the connection card and on the authorization page reads the same
 * reason and the same next step.
 */
export const ENTERPRISE_IDENTITY_NOT_ENTITLED_MESSAGE =
  'Enterprise identity is part of the Enterprise plan. Talk to your Reflex account team to turn it on for your company.';

/**
 * The companion notice for an organization with no company. Deliberately not
 * the plan message: the plan is not the problem, and telling this admin to
 * call about an upgrade would send them to the wrong place.
 *
 * Worded for every company surface, not just the identity one. It is rendered
 * on Company → Overview as well, where naming the identity provider would
 * describe a page the reader is not on.
 */
export const BILLING_ENTITY_REQUIRED_MESSAGE =
  'This organization is not part of a company yet, so there are no company settings to show. Ask your Reflex account team to attach it.';

/** The notice to render for a refusal `isUnretryableCompanyRefusal` matched. */
export function companyRefusalMessage(error: unknown): string | null {
  if (isEnterpriseIdentityNotEntitled(error)) return ENTERPRISE_IDENTITY_NOT_ENTITLED_MESSAGE;
  if (isBillingEntityRequired(error)) return BILLING_ENTITY_REQUIRED_MESSAGE;
  return null;
}
